import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { GridColumns, GridValidRowModel } from '@mui/x-data-grid-pro';
import { dateAndTimeFormatter, nestedFieldValueGetter } from 'utils/grid';
import { Chip, Tooltip, IconButton } from '@mui/material';

import {
  BLUE,
  BLUE_TERTIARY,
  GRAY,
  GRAY_LIGHT_SECONDARY,
  ORANGE_LIGHT,
  ORANGE_SECONDARY,
  PRIMARY,
  PRIMARY_LIGHT,
  RED,
  RED_LIGHT
} from 'constants/colors';
import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import { faStickyNote } from 'vendors/@fortawesome/pro-light-svg-icons';
import { StatusFilter } from '../components/StatusFilter';
import { styled } from '@mui/material/styles';
import { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 300
  }
});

export const useGetColumns = () => {
  const { t } = useTranslation('common');

  const status = {
    accepted: {
      label: 'leaveRequests.accepted',
      style: { backgroundColor: PRIMARY_LIGHT, color: PRIMARY }
    },
    rejected: {
      label: 'leaveRequests.rejected',
      style: { backgroundColor: RED_LIGHT, color: RED }
    },
    pending: {
      label: 'leaveRequests.pending',
      style: { backgroundColor: BLUE_TERTIARY, color: BLUE }
    },
    seen: {
      label: 'leaveRequests.seen',
      style: { backgroundColor: ORANGE_LIGHT, color: ORANGE_SECONDARY }
    },
    default: {
      label: 'leaveRequests.noStatus',
      style: { backgroundColor: GRAY_LIGHT_SECONDARY, color: GRAY }
    }
  };

  const renderStatusCell = (params, t) => {
    const value = params.value;
    const statusConfig = status[value] || status.default;
    const plannerNote = params.row.plannerNote || '';

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Chip
          label={t(statusConfig.label)}
          size="small"
          style={statusConfig.style}
        />
        {value === 'rejected' && (
          <CustomWidthTooltip title={plannerNote} arrow>
            <IconButton size="small" style={{ marginLeft: 8 }}>
              <FontAwesomeSvgIcon
                icon={faStickyNote}
                fontSize="inherit"
                color="action"
              />
            </IconButton>
          </CustomWidthTooltip>
        )}
      </div>
    );
  };

  const renderContractCell = (params) => {
    const {
      waNr = '',
      functionDescription = '',
      costCenterName = '',
      contractKind = ''
    } = params.row;
    return `${waNr} - ${functionDescription} - ${costCenterName} - ${contractKind}`;
  };

  return useMemo<GridColumns<GridValidRowModel>>(
    () => [
      {
        headerName: t('validFrom'),
        field: 'valid.from',
        minWidth: 110,
        type: 'date',
        flex: 1,
        valueFormatter: dateAndTimeFormatter,
        valueGetter: nestedFieldValueGetter
      },
      {
        headerName: t('validUntil'),
        field: 'valid.to',
        minWidth: 110,
        type: 'date',
        flex: 1,
        valueFormatter: dateAndTimeFormatter,
        valueGetter: nestedFieldValueGetter
      },
      {
        headerName: t('leaveType'),
        field: 'dutyTypeName',
        type: 'string',
        flex: 1
      },
      {
        headerName: t('leaveRequests.remarkEmployee'),
        field: 'empNote',
        type: 'string',
        flex: 1
      },
      {
        headerName: t('contract'),
        field: 'contractKind',
        type: 'string',
        flex: 1,
        minWidth: 375,
        valueGetter: renderContractCell
      },
      {
        headerName: t('status'),
        field: 'status',
        type: 'string',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => renderStatusCell(params, t),
        filterOperators: [
          {
            label: t('flexHistory.equals'),
            value: 'equals',
            getApplyFilterFn: (filterItem) => {
              return (row) => (row.value || 'default') === filterItem.value;
            },
            InputComponent: (props) => (
              <StatusFilter {...props} status={status} />
            )
          }
        ]
      }
    ],
    []
  );
};
